import { CompanyModel } from "../companies/company.models";
import * as moment from "moment";
import { GhgRatingModel, VesselEnergySavingEquipmentClientModel } from "../ghgs/ghg.models";
import { UnitOfWorkItem } from "../../utilities/unit-of-work-observer";
import { BaseValidationResponse } from "../validations/validations.models";
import { BaseResponse } from "../basics/basics.models";
import { SubtaskCorrespondenceEmailModel, VetTaskClientModel } from "../vetting/vetting.models";
import { IInspectionStateEntity } from "../rightship-inspections/rightship-inspections.models";
import { LookupValue } from "../lookups/lookup.models";
import { GetDynamicPropertiesResponse } from "../manage/entities.models";
import { IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";
import { IncidentGracePeriod } from '@rightship-tech/common-ui/models/incidents/incident.models';

export interface ClassSocietyClientModel {
    classId: number;
    classCode: string;
    className: string;
    classUrl: string;
    classHistoryId: number;
}

export enum CargoTypeCategoryEnum {
    Dry = 1,
    Wet = 2,
    DryWet = 3,
    NotApplicable = 4
}

export enum EnergyPowerLimitationTypeEnum {
    EnginePowerLimitation = 1,
    ShaftPowerLimitation = 2
}

export interface VesselDetailModel extends ISubtaskEntity, IInspectionStateEntity {
    shipManagerEmail?: string;
    documentOfComplianceEmail?: string;
    companiesInAbandonmentOpen?: string;
    companiesInAbandonmentClose?: string;
    vetRequiresMoreInformationEmails: SubtaskCorrespondenceEmailModel[];
    documentOfComplianceCompanyId: number;
    flagId: number;
    lastRightShipInspectionId?: number;
    leadSisterVesselId?: number;
    cargoTypeCategoryId?: number;
    vesselId: number;
    name: string;
    statCode5: string;
    bKey: string;
    internationalMaritimeOrganizationNumber: string;
    europeanNumberOfIdentification?: string;
    vehicleIdentificationNumber?: string;
    maritimeMobileServiceIdentityNumber?: string;
    dateOfBuild: Date;
    vesselTypeId?: number;
    vesselType?: string;
    vesselTypeGroupId?: number;
    vesselTypeGroup?: string;
    vesselTypeEEDIGroup?: string;
    vesselTypeEEDIGroupCode?: string;
    isDieselElectric?: boolean;
    isDirectDiesel?: boolean;
    isSteamTurbine?: boolean;
    canHaveRightShipInspection?: boolean;
    isNotStandardPropulsion?: boolean;
    pandIClubName?: string;
    propulsionTypeText?: string;
    classNarrative?: string;
    classSocieties?: ClassSocietyClientModel[];
    isTanker?: boolean;
    isBulkCarrier?: boolean;
    canRequestGhgVerification: boolean;
    hullTypeId: number;
    registeredOwnerCompanyId?: number;
    documentsCount?: number;
    privateNotesCount?: number;
    correspondenceCount?: number;
    commericalOwnerCompany?: string;
    age?: number;
    dwt?: number;
    maxDwt?: number;
    eedi?: number;
    parisMoUVisitDate?: Date;
    abjuaMoUVisitDate?: Date;
    rihadMoUVisitDate?: Date;
    hasItfAgreement?: boolean;
    exemptRightShipInspectionUntil?: Date;
    itfAgreementDate?: Date;
    supplementaryFeatures?: string[];
    hasVesselAbandonmentofSeafarersOlderThanFiveYears: boolean;
    hasCompanyAbandonmentofSeafarersOlderThanFiveYears: boolean;
    energyPowerLimitationTypeId?: number;
    energyPowerLimitationType?: string;
    eplShaPoLiDateOfImplementation?: Date;
    eplShaPoLiLastModifiedDate?: Date;
    reducedMainEngineMcr: number;
    speedMax: number;
    eexiVref: number;
    maximumSpeedAfterEpl: number;
    totalPowerOfMainEngines: number;
    companyiesInAbadonmentOlderThanFiverYears: string;
    shopTestFuelType1Id: number;
    shopTestFuelType2Id: number;
    pilotFuelType1Id: number;
    fuelType1Id: number;
    fuelType2Id: number;
    KeelLaidDateConvertedDate?: Date;
    docCompanyName?: string;
    showGracePeriodBanner: boolean;
    isSanctioned: boolean;
    isFlagFake: boolean;
    beneficialOwnerCompanyId?: number;
    beneficialOwnerCompany:string;
    isRestricted: boolean;
    isDOCRestricted: boolean;
    isBORestricted: boolean;
}

export interface AnonVesselDetailModel {
    vesselId: number;
    name: string;
    internationalMaritimeOrganizationNumber: string;
    vesselType: string;
    isDieselElectric: boolean;
    isDirectDiesel: boolean;
    isSteamTurbine: boolean;
    vesselTypeEEDIGroupCode: string;
    shopTestFuelType1Id: number;
    shopTestFuelType2Id: number;
    dwt: number;
}

export interface VesselCompanyRoleModel {
    vesselCompanyRoleId?: number;
    companyId: number;
    company: CompanyModel;
    vesselId: number;
    vesselRoleId: number;
    isActive: boolean;
    effectiveFrom: Date;
    effectiveTo?: Date;
}

export interface GetVesselApplicationFormIdFromGuidRequest {
    onboardingRequestGuid: string;
}

export interface GetVesselApplicationFormIdFromGuidResponse {
    applicationFormId: number;
    statusId: number;
    statusNotes: string;
}

export interface GetVesselRequest {
    vesselId: number;
    imo: string;
    bKey: string;
}

export interface GetVesselCompanyRolesRequest {
    vesselId: number;
}

export interface ISubtaskEntity extends IEntityTypeIdentifier {
    id?: number;
    assignedUserId?: number;
}

export interface CompCorrespondenceModel {
    correspondenceEntity: CorrespondenceEntityModel,
    isReplyShowing?: boolean,
    name: string,
    showAddRFI?: boolean,
    hasOutcome?: boolean,
    hideReply?: boolean,
    emails: SubtaskCorrespondenceEmailModel[],
    sendCloseOutEmail?: boolean,
    initialSubtaskId?: number,
    initialSubtaskLinkedEntityId?: number,
    initialSubtaskLinkedEntityTypeId?: number,
    showFilters?: boolean,
    rfiChanged?: () => void,
    newDocumentAdded: () => void
}

export interface CorrespondenceItemModel {
    name: string;
    isCustomerRFI: boolean;
    isReviewed: boolean;
    isInvitation: boolean;
    subtaskId: number;
    isInternal: boolean;
    parentSubtaskId?: number;
    entityTypeId: number;
    subtaskGuid: string;
    date: Date;
    subtaskStatusId: number;
    subtaskLinkedEntities: SubtaskLinkedEntityModel[];
    subtaskTypeIds: number[];
    subtaskStatus: string;
    subtaskStatusDate: Date;
    type: string;
    typeDisplayText: string;
    id: number;
    additionalDetails: string;
}

export interface CorrespondenceEntityModel {
    entityId: number;
    entityTypeId: number;
    isInconsistentState: boolean;
    assignment: CorrespondenceAssignmentFeatureModel;
    inspectionTypeId?: any;
    dataSources?: any;
    vetTasks?: VetTaskClientModel[];
    portCountryOfLoadSanctioned?: any;
    portCountryOfDischargeSanctioned?: any;
    portCountryOfDischargeUnknown?: any;
    portCountryOfLoadUnknown?: any;
    requestedBy: string;
    liaseWithShipManagers: boolean;
    cargoTypeCategoryId?: CargoTypeCategoryEnum;
}

export interface CorrespondenceAssignmentFeatureModel {
    isAssignedToMe: () => boolean;
    assignToMe: () => void;
    assignedTo: number;
    assignedToUserName: string;
}

export interface CorrespondenceCountsResponse {
    countByLinkedEntities: CorrespondenceCountByLinkedEntity[];
    count: number;
}

export interface PrivateNoteCountsResponse {
    count: number;
}

export interface DocumentsCountResponse {
    count: number;
}

export interface CorrespondenceCountByLinkedEntity {
    linkedEntityId: number;
    linkedEntityTypeId: number;
    count: number;
}

export interface SubtaskLinkedEntityModel {
    linkedEntityId: number;
    linkedEntityTypeId: number;
}

export interface GetVesselPerformance {
    vesselPerformanceIncidents: VesselPerformanceIncidents[];
    vesselPerformancePscs: VesselPerformancePsc[];
}

export interface VesselHeaderModel {
    isVerified: boolean;
    isSanctioned: boolean;
    isFlagFake: boolean;
    rsInspectionRequired: boolean;
    emissionsRating: string;
    emissionsRatingPlus: boolean;
    vesselType: string;
    vesselStatus: string;
    vesselStatusCode: string;
    isInGeoPoliticalZone: boolean;
    hasGeopoliticalConflict: boolean;
    dwt: number;
    maxDwt: number;
    flag: string;
    starRating: number;
    dateOfBuild: Date;
    lastRightShipInspectionId: number;
    requestedDateOfInspection: Date;
    rightShipInspectionExpiryDate: Date;
    reasonForInspection: string;
    actualDateOfInspection: Date;
    vesselId: number;
    entityId: number;
    safetyRatingType: number;
    notInService: boolean;
    rightShipInspectionStatus: string;
    requiresRightShipInspection: boolean;
    classSocieties: ClassSocietyClientModel[];
    banners: VesselHeaderBannerModel[];
    age: number;
    daysRemainingMinutely: number;
    daysRemaining: number;
    daysToAgeRequirement: number;
    inspectionState: number;
    inspectionRequired: boolean;
    imo: string;
    eni: string;
    vin: string;
    label: string;
    identifier: string;
    vesselName: string;
    officialNumber: string;
    hasPendingInspection: boolean;
    rightShipInspectionGradeCode: string;
    rightShipInspectionGradeToolTipText: string;
    rightShipInspectionGrade: string;
    useMobileInspectionWorkflow: boolean;
    isRightShipInspectionStatusAcceptable: boolean;
    isEexiCompliant: boolean;
    eexiComplianceTypeId: RightshipEexiComplianceTypeEnum;
    hideSafetyScore: boolean;
    docCompanyName?: string;
    hasCompletedVesselHealthCheck: boolean;
    lastVhcPerformedDate: Date;
    incidentGracePeriods: IncidentGracePeriod[]; 
    lastestCiiYear?: number;
    ciiRating: string;
}

export enum RightshipEexiComplianceTypeEnum {
    Eexi = 1,
    Eedi = 2,
    Evdi = 3,
}

export interface VesselHeaderBannerModel {
    response: GetBannerResponse[];
    internalName: string;
    displayName: string;
}

export interface VesselPerformanceIncidents {
    incidentsPerYear: number;
    incidentYear: number;
}
export interface VesselPerformancePsc {
    pscPerYear: number;
    inspectionYear: number;
    deficienciesPerYear: number;
    detaintionsPercentage: number;
    averageDeficienciesPerInspection: number;
    averageDetaintionsPerInspection: number;
}

export interface GetBannerResponse {
    businessUnit: string;
    internalName: string;
    displayName: string;
    companyName: string;
    countryName: string;
    listName: string;
    listId: number;
    description: string;
    isCompanyList: boolean;
    isCountrySanctioned: boolean;
    isVesselFlagSanctioned: boolean;
    bocCountrySanctioned: boolean;
    cmcCountrySanctioned: boolean;
    cocCountrySanctioned: boolean;
    docCountrySanctioned: boolean;
    rocCountrySanctioned: boolean;
    tmcCountrySanctioned: boolean;
    isFlagFake: boolean;
}

export interface BannerGrouping {
    internalName: string;
    displayName: string;
    response: GetBannerResponse[];
    bannerDueToVessel: boolean;
    bannerDueToCompany: boolean;
}

export interface BannerDescDialogModel {
    companySpecificLists: GetBannerResponse[];
    vesselSpecificLists: GetBannerResponse[];
    companyCountrySanctioned: GetBannerResponse[];
    vesselCountrySanctioned: GetBannerResponse[];
    roleCountrySanctioned: BannerRoleCountrySanctioned[];
}

export interface BannerRoleCountrySanctioned {
    role: string;
    country: string;
}

export interface DocPerformanceIncident {
    year: number;
    fleetCount: number;
    incidentCount: number;
    vesselsWithAtLeastOneIncident: number;
    vesselsWithMoreThanOneIncident: number;
    vesselsWithIncidentPercentage: number;
    vesselsWithIncidentMessage: string;
}

export interface DocPerformancePsc {
    year: number;
    fleetCount: number;
    pscInspectionCount: number;
    deficienciesCount: number;
    detentionsCount: number;
    vesselsWithNoDeficiencies: number;
    averageDeficiencies: number;
    averageDeficienciesMessage: string;

    detentionsPercentage: number;
    detentionsMessage: string;
}

export interface DocPerformanceIncidentDetail {
    vesselId: number;
    vesselName: string;
    incidentCount: number;
}

export interface GetDocPerformance {
    docPerformanceIncidents: DocPerformanceIncident[];
    docPerformancePscs: DocPerformancePsc[];
    docPerformanceIncidentDetails: DocPerformanceIncidentDetail[];
}

export interface GetGhgPerformance {
    ghgVerifiedCount: number;
    ghgUnverifiedCount: number;
    ghgPlusCount: number;
    ghgNotPlusCount: number;
    vesselTypeEEDIGroupCounts: ItemWithCount[];
    ghgRatingCounts: ItemWithCount[];
    worldFeeltGhgRatingCounts: ItemWithCount[];
}

export interface ItemWithCount {
    name: string;
    count: number;
}

export interface GetCompanyPerformanceDemography {
    companyPerformanceItems: GetCompanyPerformanceDemographyItem[];
}
export interface GetCompanyPerformanceDemographyItem {
    affiliatedVesselCount: number;
    analysisDimension: string;
    analysisValue: string;
    sequence: number;
}

export interface ActivitiesData {
    type: string;
    date: Date;
    id: number;
    toolTipTitle: string;
    toolTipDescription: string;
    markerColor: string;
    markerCount: number;
    statusId: number;
    statusName: string;
    entityTypeId: number;
}

export interface SafetySubScoreModel {
    dataTypeId: number;
    entityId: number;
    month: number;
    fleetSize: number;
    incidentSeverity: number;
    incidentCount: number;
    incidentHighSeverityCount: number;
    incidentMediumSeverityCount: number;
    incidentLowSeverityCount: number;
    detentionSeverity: number;
    detentionCount: number;
    defectsPerformance: number;
    avgDefectsByInspection: number;
    avgDefectsByInspectionAtLocation: number;
    etlProcessExecutionItemId: number;
    effectiveDate: Date;
    isCurrent: boolean;
    transparentIncidentLowSeverityCount: number;
    transparentIncidentMediumSeverityCount: number;
    transparentIncidentHighSeverityCount: number;
}

export interface GetSubscoreRatingsModelResponse {
    defectsScoringCategory?: number;
    incidentScoringCategory?: number;
    detentionsScoringCategory?: number;
    scores: SafetySubScoreModel[];
}

export interface SubscorePscInspectionDataModelResponse {
    scores: SubscorePscInspectionData[];
}

export interface SubscoreIncidentDataModelResponse {
    scores: SubscoreIncidentData[];
}

export interface SubscorePscInspectionData {
    pscInspectionBKey: string;
    pscInspectionId?: number;
    inspectionDate?: Date;
    numberOfDeficiencies?: number;
    numberOfDeficienciesOverride?: number;
    detained?: boolean;
    computedDetentionActiveInSafetyScore?: boolean;
    numberOfDaysDetained?: number;
    status: string;
    duration: string;
    portId: string;
    portAuthorityId: string;
    portName: string;
    portAuthority: string;
    averageDeficiencies?: number;
    pscSource?: number;
    pscSourceEnum?: PscSourceEnum;
    country?: string;
    countryId?: string;
    pscPerformance?: number;
    closed?: boolean;
    safetyModelHighSeverityDefectsAverage: number;
    safetyModelMedSeverityDefectsAverage: number;
    safetyModelLowSeverityDefectsAverage: number;
    numberOfDeficienciesHighSeverity: number;
    numberOfDeficienciesMedSeverity: number;
    numberOfDeficienciesLowSeverity: number;
    closeOutNumberOfDeficienciesHighSeverity: number;
    closeOutNumberOfDeficienciesMedSeverity: number;
    closeOutNumberOfDeficienciesLowSeverity: number;
    isCleanPscInspection: boolean;
    bestSafetyModelPscPerformancePercentageIncrease: number;
    actualSafetyModelPscPerformancePercentageIncrease: number;
    numberOfDeficienciesHighSeverityContributionPercentage: number;
    numberOfDeficienciesMedSeverityContributionPercentage: number;
    numberOfDeficienciesLowSeverityContributionPercentage: number;
    numberOfDeficiencies12MonthsContributionPercentage: number;
    numberOfDeficiencies24MonthsContributionPercentage: number;
    numberOfDeficiencies36MonthsContributionPercentage: number;
    numberOfDeficiencies48MonthsContributionPercentage: number;
    numberOfDeficiencies60MonthsContributionPercentage: number;
    pscCreatedDate?: Date;
    inspectionDateFormatted?: string;
    url?: string;
    bubble?: HTMLElement; 
}

export enum PscSourceEnum {
    Port = 1,
    Country = 2,
    MOU = 3
}

export interface SubscoreIncidentData {
    incidentId: number;
    incidentDate: Date;
    incidentDateFormatted: string;
    url: string;
    severity: string;
    casualtyTypeId: number;
    casualtyType: string;
    status: string;
    incidentBKey: string;
    transparentStatusBKey: string;
}

export interface RatingsSummaryModel extends IInspectionStateEntity {
    vesselEnergySavingEquipments: VesselEnergySavingEquipmentClientModel[];
    safetyRatingType: number;
    systemRuleResult: SystemRuleResultSummaryModel[];
    starRating: number;
    safetyRatingReviewId?: number;
    isVerified: boolean;
    rating: string;
    vesselId: number;
    score: number;
    docName: string;
    flagId: number;
    flagName: string;
    classId: number;
    className: string;
    sizeScore?: number;
    ratingPlus?: boolean;
    hasRating?: boolean;
    systemRuleLastExecutedDate?: Date;
    systemRuleLastExecutedStatus?: string;
    systemRuleCanRequestReview?: boolean;
    isSanctioned: boolean;
    isFlagFake: boolean;
    ghgAdditionalMetadataJSON: string;
    latestShipIndex: string;
    incidentGracePeriods: IncidentGracePeriod[];
}

export interface SystemRuleResultSummaryModel {
    canRequestReview?: boolean;
    code?: string;
    banner?: string;
    display?: string;
    group?: string;
    description?: string;
    actionTypeId?: number;
    resolveRuleDescription?: string;
}

export interface SafetyRatingModel {
    safetyRatingId: number;
    vesselId: number;
    classId?: number;
    vesselStatusId?: number;
    flagId?: number;
    docId?: number;
    overallStarRating: number;
    overallScore: number;
    scoreIncidents: number;
    scoreDetentions: number;
    scoreDefects: number;
    scoreManagementCompany: number;
    scoreFlag: number;
    scoreClass: number;
    scoringCategoryIncidents?: number;
    scoringCategoryDetentions?: number;
    scoringCategoryDefects?: number;
    scoringCategoryManagementCompany?: number;
    scoringCategoryFlag?: number;
    scoringCategoryClass?: number;
    safetyRatingReviewId?: number;
    effectiveFrom: Date;
    effectiveTo?: Date;
    systemRuleResult: string;
}

export interface GetVesselBenchmarkResponse {
    benchmarkBandEntryName: string;
    benchmarkBandName: string;
    benchmarkGroupName: string;
    measure: string;
    numberOfVessels: number;
    overallStarRating: number;
    valueFrom: number;
    valueTo: number;
    vesselTypes: string[]
}

export interface SafetyScoreChangeModel {
    effectiveFrom: Date;
    overallStarRating: number;
    overallScore: number;
    scoreIncidents: number;
    scoreDetentions: number;
    scoreDefects: number;
    scoreManagementCompany: number;
    scoreFlag: number;
    scoreClass: number;
    scoringCategoryIncidents: number;
    scoringCategoryDetentions: number;
    scoringCategoryDefects: number;
    scoringCategoryManagementCompany: number;
    scoringCategoryFlag: number;
    scoringCategoryClass: number;
    initialScoringCategoryIncidents: number;
    initialScoringCategoryDetentions: number;
    initialScoringCategoryDefects: number;
    initialScoringCategoryManagementCompany: number;
    initialScoringCategoryFlag: number;
    initialScoringCategoryClass: number;
    incidentPerformanceCountLast60Months: number;
    incidentPerformanceSeverityLast12Months: number;
    incidentPerformanceCountAndRecencyLast60Months: number;
    incidentPerformanceResponsivenessLast12Months: number;
    incidentPerformanceSeverityAndRecencyLast60Months: number;
    detentionPerformanceCountLast60Months: number;
    detentionPerformanceDurationLast12Months: number;
    detentionPerformanceCountAndRecencyLast60Months: number;
    detentionPerformanceResponsivenessLast12Months: number;
    deficienciesPerformancePSCPerformanceCountLast60Months: number;
    deficienciesPerformanceResponsivenessLast12Months: number;
    docPerformanceIncidentSeverityAndRecencyLast60Months: number;
    docPerformanceIncidentSeverityLast12Months: number;
    docPerformanceDetentionCountAndRecencyLast60Months: number;
    docPerformancePSCPerformanceLast60Months: number;
    flagPerformanceIncidentCountAndRecencyLast60Months: number;
    flagPerformanceIncidentSeverityLast12Months: number;
    flagPerformanceDetentionCountAndRecencyLast60Months: number;
    flagPerformancePSCPerformanceLast60Months: number;
    classPerformanceIncidentCountAndRecencyLast60Months: number;
    classPerformanceIncidentSeverityLast12Months: number;
    classPerformanceDetentionCountAndRecencyLast60Months: number;
    classPerformancePSCPerformanceLast60Months: number;
    initialIncidentPerformanceCountLast60Months: number;
    initialIncidentPerformanceSeverityLast12Months: number;
    initialIncidentPerformanceCountAndRecencyLast60Months: number;
    initialIncidentPerformanceResponsivenessLast12Months: number;
    initialDetentionPerformanceCountLast60Months: number;
    initialDetentionPerformanceDurationLast12Months: number;
    initialDetentionPerformanceCountAndRecencyLast60Months: number;
    initialDetentionPerformanceResponsivenessLast12Months: number;
    initialDeficienciesPerformancePSCPerformanceCountLast60Months: number;
    initialDeficienciesPerformanceResponsivenessLast12Months: number;
    initialDOCPerformanceIncidentSeverityAndRecencyLast60Months: number;
    initialDOCPerformanceDetentionCountAndRecencyLast60Months: number;
    initialDOCPerformancePSCPerformanceLast60Months: number;
    initialFlagPerformanceIncidentCountAndRecencyLast60Months: number;
    initialFlagPerformanceIncidentSeverityLast12Months: number;
    initialFlagPerformanceDetentionCountAndRecencyLast60Months: number;
    initialFlagPerformancePSCPerformanceLast60Months: number;
    initialClassPerformanceIncidentCountAndRecencyLast60Months: number;
    initialClassPerformanceIncidentSeverityLast12Months: number;
    initialClassPerformanceDetentionCountAndRecencyLast60Months: number;
    initialClassPerformancePSCPerformanceLast60Months: number;
    initialIncidentPerformanceSeverityAndRecencyLast60Months: number;
    initialIntermediateSubscoreIcsFlag: number;

    intermediateSubscoreIcsFlag: number;
    intermediateSubscoreFocFlag: number;
    excludeFromModel: number;
    excludeFromScoring: number;
    safetyRatingReviewId?: number;
    legacyRsInspection: number;
}

export interface SafetyScoreChangeVesselModel {
    rating: number;
    ratingPlus: boolean;
    internationalMaritimeOrganizationNumber: string;
    vehicleIdentificationNumber: string;
    europeanNumberOfIdentification: string;
    vesselType: string;
    deadWeightTonnage: number;
    dateOfBuild: Date;
    safetyRatingType: number;
    hasError: boolean;
    starRating: number;
    vesselName: string;
    vesselId: number;
}

export interface GetSafetyScoreChangeModelResponse {
    vesselDetails: SafetyScoreChangeVesselModel;
    current: SafetyRatingChangedItem;
    previous: SafetyRatingChangedItem;
}

export interface SafetyRatingChangedItem {
    rating: SafetyScoreChangeModel;
    docName: string;
    flagName: string;
    className: string;
    safetyRatingId: number;
    safetyRatingReviewId?: number;
}

export interface GetModelLastRunResponse {
    modelLastUpdated: Date;
    dataLastUpdated: Date;
    modelVersion: string;
}

export interface GetSafetyRatingResponse {
    safetyRating: SafetyRatingModel;
    safetyRatingType: number;
    inspectionState: number;
    docHybridRules: DocHybridRule[];
    noScoreReasons: string;
    openPscCount: number;
}

export interface DocHybridRule {
    docCompanySkey: number;
    hybridRuleCode: string;
    hybridRuleName: string;
    hybridRuleDescription: string;
}

export interface GetVesselResponse {
    vessel: VesselDetailModel;
    certificates: VesselCertificateModel[];
    crewWelfareStatus: string;
    crewWelfareValidUntil: Date;
    isLegacyCrewWelfare: boolean;
    crewWelfareGuid: string;
}

export interface GetVesselAnonymouslyResponse {
    vessel: AnonVesselDetailModel;
}

export interface VesselCertificateModel {
    certificateUID: string;
    certificate: string;
    certificateMeta: VesselCertificateMetaModel;
}

export interface VesselCertificateMetaModel {
    logo: string;
    title: string;
    vesselDescription: string;
}

export interface VesselGroupedCertificateModel {
    title: string;
    desc: string;
    imgUrl: string;
    companies: string[];
}

export interface GetVesselCompanyRolesResponse {
    vesselCompanyRoles: VesselCompanyRoleModel[];
}

export interface VesselODataModel {
    vesselId: number;
    safetyRating: number;
    ghgRating: string;
    ghgRatingWeight: number;
    name: string;
    internationalMaritimeOrganizationNumber: string;
    dwt: number;
    dateOfBuild: Date;
    yearOfConstruction: number;
    shipType: string;
    shipTypeId: number;
    flagCountry: string;
    flagCountryId: number;
    classCode: string;
    shipStatusId: number;
    shipStatus: string;
    isEexiCompliant: boolean;
    eexiComplianceTypeId: RightshipEexiComplianceTypeEnum;
}

export interface VesselEnergySavingEquipmentModel {
    vesselEnergySavingEquipmentId: number;
    vesselId: number;
    energySavingEquipmentCategoryId: number;
    energySavingEquipmentId: number;
    comments: string;
    ghgRatingFactorSourceId: number;
    ghgRatingFactorSourceComment: string;
}

export interface GetVesselTypesGHGRatingPeerGroupModel {
    name: string;
    code: string;
}

export interface GetVesselGhgSummary {
    ghgRating: GhgRatingModel;
    vesselEnergySavingEquipments: VesselEnergySavingEquipmentModel[];
    vesselTypeEEDIGroup: string;
    ghgRatingFactors: GhgRatingFactorClientModel[];
    vesselTypeEEDIGroupCode: string;
    isDirectDiesel: boolean;
    isDieselElectric: boolean;
    isShuttleTanker: boolean;
    isSteamTurbine: boolean;
}

export interface GetVesselOperationalMetricsResponse {
    operationalMetricsDetails: VesselOperationalMetricsDetails;
    operationalMetricsChartData: VesselOperationalMetricsChartData[];
    operationalMetricsPeerGroups: VesselOperationalMetricsPeerGroup[];
    vesselCiiHistories: VesselCiiHistory[];
}

export interface VesselOperationalMetricsDetails {
    attainedEexi: number;
    requiredEexi: number;
    energyPowerLimitationTypeId: number;
    reducedMainEngineMcr: number;
    maximumSpeedAfterEpl: number;
    originalMaximumSpeed: number;
    originalMainEngineMcr: number;
    mainEngineMcrWithoutEpl: number;
    attainedCii: number;
    requiredCii: number;
    evdi: number;
    tonnageValue: number;
    isEexiCompliant: boolean;
    eexiComplianceTypeId: number;
    ciiRatingYear: number;
    complianceIssueDate: Date;
}

export interface VesselCiiHistory {
    attainedCii: number;
    ciiRating: string;
    ciiRatingYear: number;
    ciiStatusId: number
    complianceIssueDate: Date;
}

export enum CiiStatusEnum {
    Certified = 1
}

export interface VesselOperationalMetricsChartData {
    eexi: number;
    tonnageValue: number;
}

export interface VesselOperationalMetricsPeerGroup {
    eexi: number;
    tonnageValue: number;
    eexiChartDataTonnageTypeId: number;
}

export interface GetVesselGHGRatingPeerGroupResponse {
    items: GHGRatingPeerGroupItem[];
    vesselTypeEEDIPeeringGroupCode: string;
}

export interface GHGRatingPeerGroupItem {
    weight: number;
    sci: number;
    latestShipIndex: string;
    ghgRating: string;
    vesselId: number;
    vesselImo: string;
    vesselName: string;
}

export interface GhgRatingFactorClientModel {
    currentValue: any;
    dataSource: string;
    lastModifiedDate: string;
    group: string;
    propertyName: string;
    vesselProperty: string;
    displayName: string;
    dataType: number;
    lookupMappedToProperty: string;
    isVisible: boolean;
    entityFinderMappedToProperty: string;
    isNewProperty?: boolean;
}

export interface GhgRatingEplShaPoLiDetails {
    energyPowerLimitationTypeId: boolean;
    eplShaPoLiDateOfImplementation: Date;
    eplShaPoLiLastModifiedDate: Date;
}

export class VesselHelper {
    //to keep vessel age consistent across the app
    static getAgeFromDate(date): string {
        return (Math.floor(moment().diff(date, 'year', true) * 10) / 10).toFixed(1) + "y/o" + " (" + moment(date).year() + ")"
    }
}

export interface VesselLODActivityClientModel extends BaseUscgData {
    vesselLODActivityId: number;
}

export interface VesselCOTPActivityClientModel extends BaseUscgData {
    vesselCOTPActivityId: number;
}

export interface BaseUscgData {
    imposedDateTime: Date;
    removedDateTime: Date;
    isClosed: boolean;
    comments: string;
    unit: string;
    type: string;
    reason: string;
    category: string;
    vesselId: number;
    createdDate: Date;
    lastModifiedDate: Date;
    externalId: number;
}

export class VesselCapSurveyClientModel {
    vesselCapSurveyId?: number;
    vesselId: number;
    vessel?: string;
    issuedById: number;
    issuedBy?: string;
    dateIssued: Date;
    completedDate?: Date;
    hullRatingId?: number;
    hullRating?: string;
    machineryCargoSystemRatingId?: number;
    machineryCargoSystemRating?: string;
    machineryCargoSystemRatingCompletedDate?: Date;
    sourceId?: number;
    source?: string;
}

export class VesselClassSurveyClientModel {
    vesselClassSurveyId: number;
    classificationSocietyId: number;
    classificationSociety: string;
    description: string;
    lastInspected: Date;
    dueDate: Date;
    dueDateRangeFrom: Date;
    dueDateRangeTo: Date;
    postponedUntilDate: Date;
    createdDate: Date;
    vesselId: number;
}

export class VesselClassCertificateClientModel {
    classificationSocietyId: number;
    classificationSociety: string
    name: string;
    issuedDate: Date;
    expiryDate: Date;
    postponedUntilDate: Date;
    vesselClassCertificateId: number;
    createdDate: Date;
    certificateType: string;
    vesselId: number;
}

export interface SaveCAPSurveyRequest {
    id: number;
    changes: { [index: string]: UnitOfWorkItem };
}

export interface DeleteCAPSurveyResponse {
    isSuccess: boolean;
}

export interface GetVesselSurveysAndCertificatesResponse extends BaseResponse {
    vesselCapSurveys: VesselCapSurveyClientModel[];
    vesselClassSurveys: VesselClassSurveyClientModel[];
    vesselClassCertificates: VesselClassCertificateClientModel[];
}

export interface GetVesselUscgResponse extends BaseResponse {
    lods: VesselLODActivityClientModel[];
    cotps: VesselCOTPActivityClientModel[];
}

export interface SaveCAPSurveyResponse extends BaseValidationResponse {
    item: VesselCapSurveyClientModel;
    action?: number;
}

export class VesselThirdPartyInspectionClientModel {
    vesselThirdPartyInspectionId?: number;
    vesselId: number;
    vessel?: string;
    inspectingCompanyId: number;
    inspectingCompany?: string;
    thirdPartyInspectionTypeId: number;
    thirdPartyInspectionType?: string;
    inspectionDate: Date;
    vesselOperationAtTimeOfInspectionId?: number;
    vesselOperationAtTimeOfInspection?: string;
    productBeingHandled?: string;
    satisfactoryWithOperatorComments?: boolean;
    comments?: string;
}

export interface SaveThirdPartyInspectionRequest {
    id: number;
    changes: { [index: string]: UnitOfWorkItem };
}

export interface DeleteThirdPartyInspectionResponse {
    isSuccess: boolean;
}

export interface SaveThirdPartyInspectionResponse extends BaseValidationResponse {
    item: VesselThirdPartyInspectionClientModel;
    action?: number;
}

export enum VesselMousEnum {
    Paris = 1,
    Riyadh,
    Abuja,
    Caribbean,
    Tokyo,
    Amsa,
    BlackSea
}

export interface VesselMouClientModel {
    vesselId: number;
    mouName: string;
    mou?: VesselMousEnum;
    lastVisitedDate?: Date;
    lastClearedDate?: Date;
}

export interface VesselMouClearanceLogClientModel {
    vesselMouClearanceLogId: number;
    vesselId: number;
    vessel: string;
    mou: string;
    clearedDate: Date;
    userId: number;
    user: string;
}

export interface GetVesselMousRequest {
    vesselId: number;
}

export interface GetVesselMousResponse {
    vesselMous: VesselMouClientModel[];
}

export interface ClearVesselMouRequest {
    vesselId: number;
    mou?: VesselMousEnum;
}

export interface ClearVesselMouResponse {
    clearanceLogs: VesselMouClearanceLogClientModel[];
    isSuccessFull: boolean;
}

export interface GetVesselClassHistoryResponse {
    vesselClassHistoryChanges: VesselClassHistoryChange[];
}

export interface VesselClassHistoryChange {
    addedClassValue: string;
    deletedClassValue: string;
    date: Date | string;
    isActive: boolean;
}

export interface SaveVesselRequest {
    vessel: AddVesselModel;
}

export interface AddVesselModel {
    vesselName: string;
    imo: string;
    eni: string;
    vin: string;
    vesselStatusId: number;
    vesselStatusCode?: string;
    vesselTypeId: number;
    vesselTypeCode?: string;
    flagId: number;
    flagCode?: string;
    docCompanyId: number;
    docCompanyCode?: string;
    dateOfBuild: Date;
    dwt: number;
    nrt: number;
    loa: number;
    breadthMoulded: number;
    breadthExtreme: number;
    draught: number;
    hullTypeId: number;
    hullTypeCode?: string;
}

export interface SaveClassSurveyRequest {
    id: number;
    changes: { [index: string]: UnitOfWorkItem };
}

export interface SaveClassSurveyResponse extends BaseValidationResponse {
    item: VesselClassSurveyClientModel;
    action?: number;
}

export interface DeleteClassSurveyResponse {
    isSuccess: boolean;
}

export interface SaveClassCertificateRequest {
    id: number;
    changes: { [index: string]: UnitOfWorkItem };
}

export interface SaveClassCertificateResponse extends BaseValidationResponse {
    item: VesselClassCertificateClientModel;
    action?: number;
}

export interface DeleteClassCertificateResponse {
    isSuccess: boolean;
}

export interface GetVesselDynamicPropertiesResponse {
    manageEntityGroups: LookupValue[];
    dynamicPropertiesResponse: GetDynamicPropertiesResponse;    
}

export interface CloseGracePeriodBannerRequest {
    vesselId: number;
}

export interface GetVesselsByCompanyIdResponse {
    vessels: ExtractedVessel[];
}

export interface ExtractedVessel {
    id: number;
    name: string;
    internationalMaritimeOrganizationNumber: string;
    vehicleIdentificationNumber: string;
    europeanNumberOfIdentification: string;
    maritimeMobileServiceIdentityNumber: string;
    deadweightTonnage?: number;
    vesselTypeValue: string;
}

export interface ExtractVesselsFromFileRequest {
    file: File;
    customerId: number;
}

export interface ExtractVesselsFromFileResponse extends BaseValidationResponse {
    vessels: ExtractedVessel[];
    notFoundVesselImos: string[];
}